import { DerivedKeys } from "../crypto/encryption"
import { decrypt, encrypt } from "../crypto/encryption"

interface AuthData {
  token: string | null
  derivedKeys: DerivedKeys | null
  email: string
}

export interface ObfuscatedData {
  obfuscatedEmail: string
  encryptedData: string | null
}

export abstract class AuthStorage {
  // Core methods to be implemented by derived classes
  protected abstract getPersistentItem(key: string): string | null
  protected abstract setPersistentItem(key: string, value: string): void
  protected abstract removePersistentItem(key: string): void
  protected abstract getSessionItem(key: string): string | null
  protected abstract setSessionItem(key: string, value: string): void
  protected abstract removeSessionItem(key: string): void

  // Keys for storage
  private readonly PREFIX = "taskfortress"
  private readonly AUTH_DATA_KEY = `${this.PREFIX}:authData`
  private readonly OBFUSCATED_EMAIL_KEY = `${this.PREFIX}:obfuscatedEmail`
  private readonly PASSWORD_KEY = `${this.PREFIX}:password`

  /**
   * Check if user is authenticated (has encrypted auth data and session password)
   */
  isAuthenticated(): boolean {
    const hasEncryptedData = this.getPersistentItem(this.AUTH_DATA_KEY) !== null
    const hasPasswordInSession = this.getSessionItem(this.PASSWORD_KEY) !== null
    return hasEncryptedData && hasPasswordInSession
  }

  /**
   * Check if user has an account (has obfuscated email stored)
   */
  hasAccount(): boolean {
    return this.getPersistentItem(this.OBFUSCATED_EMAIL_KEY) !== null
  }

  /**
   * Obfuscate an email address for display
   */
  private obfuscateEmail(email: string): string {
    if (!email || !email.includes("@")) return ""

    const [username, domain] = email.split("@")

    // Obfuscate username
    let obfuscatedUsername: string
    if (username.length <= 3) {
      obfuscatedUsername = `${username.charAt(0)}***`
    } else {
      obfuscatedUsername = `${username.charAt(0)}***${username.charAt(username.length - 1)}`
    }

    // Obfuscate domain
    const domainParts = domain.split(".")
    let obfuscatedDomain: string

    if (domainParts.length === 1) {
      // Simple domain without TLD
      obfuscatedDomain =
        domainParts[0].length <= 2
          ? `${domainParts[0].charAt(0)}***`
          : `${domainParts[0].charAt(0)}***`
    } else {
      // Domain with TLD - keep the TLD visible
      const mainDomain = domainParts.slice(0, -1).join(".")
      const tld = domainParts[domainParts.length - 1]
      obfuscatedDomain =
        mainDomain.length <= 2
          ? `${mainDomain.charAt(0)}***.${tld}`
          : `${mainDomain.charAt(0)}***.${tld}`
    }

    return `${obfuscatedUsername}@${obfuscatedDomain}`
  }

  /**
   * Store encrypted auth data in persistent storage
   */
  async storeAuthData(authData: AuthData, password: string): Promise<void> {
    if (!authData.email) throw new Error("Email is required")
    if (!password) throw new Error("Password is required")

    // Store obfuscated email
    const obfuscatedEmail = this.obfuscateEmail(authData.email)
    this.setPersistentItem(this.OBFUSCATED_EMAIL_KEY, obfuscatedEmail)

    // Convert auth data to JSON and encrypt it
    const dataJson = JSON.stringify(authData)

    // Convert password to key bytes for encryption
    const encoder = new TextEncoder()
    const passwordBytes = encoder.encode(password)

    // Hash the password to get a proper encryption key (32 bytes)
    const passwordKey = await crypto.subtle.digest("SHA-256", passwordBytes)

    // Encrypt the auth data
    const encryptedData = await encrypt(dataJson, new Uint8Array(passwordKey))

    // Store the encrypted data
    this.setPersistentItem(this.AUTH_DATA_KEY, encryptedData)
  }

  /**
   * Retrieve and decrypt auth data from persistent storage
   */
  async getAuthData(password: string): Promise<AuthData | null> {
    const encryptedData = this.getPersistentItem(this.AUTH_DATA_KEY)
    if (!encryptedData) return null

    try {
      // Convert password to key bytes for decryption
      const encoder = new TextEncoder()
      const passwordBytes = encoder.encode(password)

      // Hash the password to get a proper decryption key (32 bytes)
      const passwordKey = await crypto.subtle.digest("SHA-256", passwordBytes)

      // Decrypt the auth data
      const decryptedData = await decrypt(
        encryptedData,
        new Uint8Array(passwordKey),
      )

      // Parse the decrypted JSON
      const j = JSON.parse(decryptedData) as any
      if (j.derivedKeys) {
        j.derivedKeys = DerivedKeys.fromJSON(j.derivedKeys)
      }
      return j as AuthData
    } catch (error) {
      console.error("Failed to decrypt auth data:", error)
      return null
    }
  }

  /**
   * Store password in session storage
   */
  storePassword(password: string): void {
    if (!password) return
    this.setSessionItem(this.PASSWORD_KEY, password)
  }

  /**
   * Get password from session storage
   */
  getPassword(): string | null {
    return this.getSessionItem(this.PASSWORD_KEY)
  }

  /**
   * Get obfuscated email from persistent storage
   */
  getObfuscatedEmail(): string | null {
    return this.getPersistentItem(this.OBFUSCATED_EMAIL_KEY)
  }

  /**
   * Get raw encrypted data and obfuscated email
   */
  getObfuscatedData(): ObfuscatedData {
    return {
      obfuscatedEmail: this.getPersistentItem(this.OBFUSCATED_EMAIL_KEY) || "",
      encryptedData: this.getPersistentItem(this.AUTH_DATA_KEY),
    }
  }

  /**
   * Clear all auth data from storage
   */
  clearAllAuth(): void {
    this.removePersistentItem(this.AUTH_DATA_KEY)
    this.removePersistentItem(this.OBFUSCATED_EMAIL_KEY)
    this.removeSessionItem(this.PASSWORD_KEY)
  }
}
